import React from 'react';
import { ExclamationTriangleFill, Trophy } from 'react-bootstrap-icons';
import { useLocale } from '../../i18n/LocaleContext';
import { Avatar, ProductThumb } from './WinnerBits';
import { formatPrice } from './wheelProducts';
import { fmtNum } from './wheelUi';

/**
 * The wheel's rounds, each with its products and — once drawn — its winner
 * (contract v2: one round = one winner who gets all of the round's products).
 *   winners   normalised winners (prizeWheelShape.normalizeWheelWinner) | null
 *   state     'idle' | 'loading' | 'ok' | 'error' (the /winners read)
 *   showWinners  false on a wheel that has not been drawn yet
 */
export default function RoundResults({ wheel, winners = null, state = 'ok', showWinners = true }) {
  const { t, lang } = useLocale();
  if (showWinners && state === 'loading') return <div className="panel-card__muted">{t('shell.loading')}</div>;
  if (showWinners && state === 'error') {
    return <div className="gifts__error" role="alert"><ExclamationTriangleFill size={14} /> {t('wheel.winnersError')}</div>;
  }
  const rounds = wheel.rounds?.length
    ? wheel.rounds
    : Array.from({ length: wheel.winnerCount || 0 }, (_, i) => ({ round: i + 1, products: [] }));
  const byRound = new Map((winners || []).map((w, i) => [w.round ?? i + 1, w]));
  if (showWinners && wheel.participantCount === 0 && !(winners && winners.length)) {
    return (
      <div className="gifts__empty wheel-winners__empty">
        <Trophy size={20} aria-hidden="true" />
        <span>{t('wheel.noParticipants')}</span>
      </div>
    );
  }
  return (
    <ol className="wheel-results">
      {rounds.map((r) => {
        const w = byRound.get(r.round);
        return (
          <li key={r.round} className={`wheel-results__row${w ? ' has-winner' : ''}`}>
            <span className="wheel-winners__round" title={t('wheel.round', { n: r.round })}>{r.round}</span>
            <div className="wheel-results__prize">
              <div className="wheel-results__thumbs">
                {r.products.slice(0, 3).map((p) => <ProductThumb key={p.id} product={p} />)}
                {r.products.length > 3 && <span className="wheel-more">+{r.products.length - 3}</span>}
              </div>
              <div className="wheel-results__names">
                {r.products.length ? (
                  r.products.map((p) => (
                    <span key={p.id} className="wheel-results__product">
                      {p.name} <span className="gift-muted">{formatPrice(p, lang)}</span>
                    </span>
                  ))
                ) : (
                  <span className="gift-muted">{t('wheel.round', { n: r.round })}</span>
                )}
              </div>
            </div>
            {showWinners && (
              <div className="wheel-results__winner">
                {w ? (
                  <>
                    <Avatar winner={w} />
                    <span className="wheel-winners__who">
                      <span className="wheel-winners__name">{w.anonymous || !w.name ? t('wheel.someone') : w.name}</span>
                      <span className="wheel-winners__meta">
                        {w.username && <span>@{w.username}</span>}
                        {w.anonymous && <span>{t('wheel.anonymous')}</span>}
                        {w.entryIndex !== null && <span title={t('wheel.entryIndexTitle')}>#{fmtNum(w.entryIndex, lang)}</span>}
                      </span>
                    </span>
                  </>
                ) : (
                  <span className="gift-muted">{t('wheel.noWinnerRound')}</span>
                )}
              </div>
            )}
          </li>
        );
      })}
    </ol>
  );
}
